import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import {
    getCurrentPromotionsDataView,
    getDiscountTotal,
    getOrderPromotions,
    getPromotionsDataView,
    getShippingPromotions,
} from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CartTotalDisplay, { CartTotalDisplayStyles } from "@insite/content-library/Components/CartTotalDisplay";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const { cartId } = state.pages.checkoutShipping;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const promotions = cartId ? getPromotionsDataView(state, cartId).value : getCurrentPromotionsDataView(state).value;
    const { isUpdatingCart } = state.pages.checkoutShipping;
    return {
        cart: cartState.value,
        orderPromotions: promotions ? getOrderPromotions(promotions) : undefined,
        shippingPromotions: promotions ? getShippingPromotions(promotions) : undefined,
        discountTotal: promotions ? getDiscountTotal(promotions) : undefined,
        isLoading: !cartState.value || cartState.isLoading || isUpdatingCart,
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

export interface CheckoutShippingCartTotalStyles {
    container?: GridContainerProps;
    cartTotalGridItem?: GridItemProps;
    cartTotal?: CartTotalDisplayStyles;
    buttonsGridItem?: GridItemProps;
    continueButton?: ButtonPresentationProps;
}

export const cartTotalStyles: CheckoutShippingCartTotalStyles = {
    container: {
        gap: 10,
        css: css`
            padding-top: 20px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    null,
                    css`
                        & + div[class^="GridWrapper"] .checkout-custom-footer {
                            display: none;
                        }
                    `,
                    css`
                        & + div[class^="GridWrapper"] .checkout-custom-footer {
                            display: none;
                        }
                    `,
                ])};
        `,
    },
    cartTotalGridItem: { width: 12 },
    buttonsGridItem: {
        width: 12,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            display: none;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    continueButton: {
        variant: "primary",
        css: css`
            margin-top: 10px;
            &.buttonTransition {
                background: linear-gradient(to left, #ea636b 50%, #ef3742 50%);
                background-size: 200% 100%;
                background-position: left bottom;
                animation: gradient-animation 9s ease-in-out infinite;
                @keyframes gradient-animation {
                    0% {
                        background-position: right bottom;
                    }
                    100% {
                        background-position: left top;
                    }
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    css`
                        width: 100%;
                    `,
                    css`
                        width: 100%;
                    `,
                    css`
                        width: 100%;
                    `,
                    css`
                        width: 100%;
                    `,
                ])}
        `,
    },
};

const styles = cartTotalStyles;

const CheckoutShippingCartTotal: FC<Props> = ({
    cart,
    orderPromotions,
    shippingPromotions,
    discountTotal,
    isLoading,
}) => {
    const isAddressLoaded =
        typeof document !== "undefined" &&
        !!(
            document.querySelector('[data-test-selector="checkoutShipping_billingAddress"]') &&
            document.querySelector('[data-test-selector="checkoutShipping_shippingAddress"]')
        );
    const handleContinueClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        const button = event.currentTarget;
        button.classList.add("buttonTransition");
    };
    return (
        <GridContainer data-test-selector="checkoutShippingCartTotal" {...styles.container}>
            <GridItem {...styles.cartTotalGridItem}>
                <CartTotalDisplay
                    extendedStyles={styles.cartTotal}
                    cart={cart}
                    orderPromotions={orderPromotions}
                    shippingPromotions={shippingPromotions}
                    discountTotal={discountTotal}
                />
            </GridItem>
            <GridItem {...styles.buttonsGridItem}>
                <Button
                    {...styles.continueButton}
                    type="submit"
                    disabled={isLoading || !isAddressLoaded}
                    data-test-selector="checkoutShippingCartTotalContinueButton"
                    onClick={handleContinueClick}
                >
                    {translate("Continue")}
                </Button>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(CheckoutShippingCartTotal),
    definition: {
        group: "Checkout - Shipping",
        allowedContexts: ["CheckoutShippingPage"],
        displayName: "Cart Total",
    },
};

export default widgetModule;
